import React from 'react';
import type { Theme } from '@material-ui/core';
import { Grid, Paper } from '@material-ui/core';
import { Block, Typography } from '@klp/design';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import { useTranslation } from 'react-i18next';

import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { KlpFontVariant } from '@klp/design/Theme/KlpFontVariant';
import { useNokkeltallSammendrag } from '../../context/useNokkeltallSammendrag';

const styles = (theme: Theme) => ({
    paper: {
        height: '100%',
    },
    heading: {
        fontWeight: 600,
        fontSize: KlpFontVariant.epsilon.fontSize,
        marginBottom: theme.spacing(1),
    },
    itemText: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    valueText: {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    // Timeanslaget står dempet foran beløpet, som i skissen fra bestiller.
    hoursText: {
        color: theme.palette.text.secondary,
    },
});

/**
 * De to prosentbaserte tilleggene, vist under kostnadstabellen. Beløpene er
 * backend-beregnede og følger derfor samme debounce som resten av nøkkeltallene.
 */
export const TilleggSammendrag: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { nokkeltall } = useNokkeltallSammendrag();

    if (!nokkeltall) return null;

    const currency = ' kr';

    // Eksplisitt entall/flertall framfor i18next sine plural-suffikser: begge
    // nøklene må finnes bokstavelig for at validate-translations.js skal se dem.
    const timeEnhet =
        nokkeltall.prosjektlederTimer === 1
            ? t('prosjekt.kostnadskalkyle.tillegg.time')
            : t('prosjekt.kostnadskalkyle.tillegg.timer');

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={4}>
                <Paper className={classes.paper}>
                    <Block padding="tiny">
                        <Block padding="tiny">
                            <Typography
                                className={classes.heading}
                                translation="prosjekt.kostnadskalkyle.tillegg.title"
                            />
                            <Typography className={classes.itemText}>
                                {t('prosjekt.kostnadskalkyle.tillegg.risiko')}:
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={nokkeltall.risikotillegg}
                                    suffix={currency}
                                />
                            </Typography>
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.tillegg.prosjektleder',
                                )}
                                :
                                <span>
                                    <span className={classes.hoursText}>
                                        {nokkeltall.prosjektlederTimer}{' '}
                                        {timeEnhet} /{' '}
                                    </span>
                                    <NumberFormatted
                                        className={classes.valueText}
                                        value={nokkeltall.prosjektlederTillegg}
                                        suffix={currency}
                                    />
                                </span>
                            </Typography>
                        </Block>
                    </Block>
                </Paper>
            </Grid>
        </Grid>
    );
};
